import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  DollarSign, TrendingUp, Gift, Ticket, ArrowUpRight, 
  Download, CreditCard, Wallet, Clock, CheckCircle,
  AlertCircle, ExternalLink, ChevronRight, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { PartnerLayout } from '@/components/PartnerLayout';
import { DateRangePicker } from '@/components/DateRangePicker';
import api from '@/lib/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#6366f1', '#8b5cf6', '#10b981', '#f59e0b'];

const SummaryCard = ({ title, value, subtitle, icon: Icon, color = 'indigo', trend }) => {
  const colorClasses = {
    indigo: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400',
    green: 'bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-950 dark:text-purple-400',
    orange: 'bg-orange-100 text-orange-600 dark:bg-orange-950 dark:text-orange-400',
  };

  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
          {trend && (
            <Badge variant="secondary" className="bg-green-100 text-green-700 border-0">
              <ArrowUpRight className="w-3 h-3 mr-1" />
              {trend}%
            </Badge>
          )}
        </div>
        <div className="mt-4">
          <p className="text-3xl font-bold text-foreground">{value}</p>
          <p className="text-sm font-medium text-foreground mt-1">{title}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );
};

const PayoutStatusCard = ({ payoutInfo, onSetupClick }) => {
  const isConnected = payoutInfo?.stripe_connected && payoutInfo?.stripe_onboarding_complete;
  
  return (
    <Card className={`border-2 ${isConnected ? 'border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/20' : 'border-orange-200 bg-orange-50/50 dark:border-orange-800 dark:bg-orange-950/20'}`}>
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isConnected ? 'bg-green-100 dark:bg-green-900' : 'bg-orange-100 dark:bg-orange-900'}`}>
            {isConnected ? (
              <CheckCircle className="w-6 h-6 text-green-600" />
            ) : (
              <AlertCircle className="w-6 h-6 text-orange-600" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-foreground">
              {isConnected ? 'Payouts Active' : 'Setup Payouts'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {isConnected 
                ? 'Your Stripe Connect account is verified. Payouts are processed automatically.'
                : 'Complete Stripe Connect setup to receive automatic payouts.'
              }
            </p>
            {!isConnected && (
              <Button 
                className="mt-3 bg-orange-600 hover:bg-orange-700" 
                size="sm"
                onClick={onSetupClick}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Complete Setup
                <ExternalLink className="w-3 h-3 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const TransactionRow = ({ transaction }) => {
  const isEvent = transaction.type === 'ticket';
  
  return (
    <div className="flex items-center justify-between p-4 border-b last:border-b-0 hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isEvent ? 'bg-indigo-100 dark:bg-indigo-950' : 'bg-purple-100 dark:bg-purple-950'}`}>
          {isEvent ? (
            <Ticket className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          ) : (
            <Gift className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          )}
        </div>
        <div>
          <p className="font-medium text-foreground">{transaction.item_title}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{new Date(transaction.date).toLocaleDateString()}</span>
            <span>•</span>
            <span>{transaction.quantity}x</span>
          </div>
        </div>
      </div>
      <div className="text-right">
        <p className="font-bold text-green-600">${transaction.net_payout?.toFixed(2)}</p>
        <p className="text-xs text-muted-foreground">
          ${transaction.gross_amount?.toFixed(2)} - ${transaction.platform_fee?.toFixed(2)} fee
        </p>
      </div>
    </div>
  );
};

const RevenueBreakdownChart = ({ experiences, events }) => {
  const chartData = [
    { 
      name: 'Experiences', 
      value: experiences?.gross_revenue || 0,
      net: experiences?.net_payout || 0,
      sold: experiences?.total_sold || 0
    },
    { 
      name: 'Events', 
      value: events?.gross_revenue || 0,
      net: events?.net_payout || 0,
      sold: events?.total_sold || 0
    }
  ];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Revenue by Type</CardTitle>
        <CardDescription>Experiences vs Event Tickets</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={5}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => [
                  `$${value.toFixed(2)} gross`, 
                  name
                ]}
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-indigo-500" />
            <span className="text-sm text-muted-foreground">Experiences</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-500" />
            <span className="text-sm text-muted-foreground">Events</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const TopItemsTable = ({ items, type }) => {
  const isExperience = type === 'experience';
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Top {isExperience ? 'Experiences' : 'Events'}</CardTitle>
          {isExperience ? (
            <Gift className="w-5 h-5 text-purple-600" />
          ) : (
            <Ticket className="w-5 h-5 text-indigo-600" />
          )}
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No {type} sales yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.slice(0, 5).map((item, i) => (
              <div key={item.id || i} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  i === 0 ? 'bg-yellow-400 text-yellow-900' :
                  i === 1 ? 'bg-gray-300 text-gray-700' :
                  i === 2 ? 'bg-orange-400 text-orange-900' :
                  'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                }`}>
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{item.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {isExperience ? `${item.quantity_sold} sold` : `${item.tickets_sold} tickets`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">${item.net_revenue?.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">net</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const FeeExplainer = () => (
  <Card className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
    <CardHeader className="pb-2">
      <CardTitle className="text-lg flex items-center gap-2">
        <DollarSign className="w-5 h-5" />
        Fee Structure
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="p-4 bg-white dark:bg-slate-950 rounded-lg border">
        <div className="flex items-center gap-2 mb-2">
          <Ticket className="w-4 h-4 text-indigo-600" />
          <span className="font-medium">Event Tickets</span>
        </div>
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">3.5% + $1.59</span> service fee per ticket
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Fee is added to ticket price (paid by customer). You receive 100% of face value.
        </p>
      </div>
      
      <div className="p-4 bg-white dark:bg-slate-950 rounded-lg border">
        <div className="flex items-center gap-2 mb-2">
          <Gift className="w-4 h-4 text-purple-600" />
          <span className="font-medium">Experiences/Vouchers</span>
        </div>
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">25%</span> flat commission
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Commission deducted from sale price. You receive 75% of each sale.
        </p>
      </div>
    </CardContent>
  </Card>
);

const PartnerRevenue = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isPartner, user, loading: authLoading, refreshUser } = useAuth();
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (authLoading) return;
      
      if (!isAuthenticated) {
        navigate('/auth');
        return;
      }
      
      if (!user) {
        try {
          await refreshUser();
        } catch (error) {
          navigate('/auth');
          return;
        }
      }
      
      if (!isPartner) {
        navigate('/dashboard');
        return;
      }
      
      fetchRevenue();
    };
    
    init();
  }, [isAuthenticated, isPartner, authLoading, user]);

  // Refetch when date range changes
  useEffect(() => {
    if (isAuthenticated && isPartner) {
      fetchRevenue();
    }
  }, [dateRange]);

  const fetchRevenue = async () => {
    try {
      setLoading(true);
      let url = '/partner/revenue';
      const params = new URLSearchParams();
      
      if (dateRange?.from) {
        params.append('start_date', format(dateRange.from, 'yyyy-MM-dd'));
      }
      if (dateRange?.to) {
        params.append('end_date', format(dateRange.to, 'yyyy-MM-dd'));
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await api.get(url);
      setRevenueData(response.data);
    } catch (error) {
      console.error('Failed to fetch revenue data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      let url = '/partner/revenue/export';
      const params = new URLSearchParams();
      
      if (dateRange?.from) {
        params.append('start_date', format(dateRange.from, 'yyyy-MM-dd'));
      }
      if (dateRange?.to) {
        params.append('end_date', format(dateRange.to, 'yyyy-MM-dd'));
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      // Fetch CSV data
      const response = await api.get(url, { responseType: 'blob' });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Generate filename
      let filename = `revenue_export_${format(new Date(), 'yyyyMMdd')}`;
      if (dateRange?.from) filename += `_from_${format(dateRange.from, 'yyyy-MM-dd')}`;
      if (dateRange?.to) filename += `_to_${format(dateRange.to, 'yyyy-MM-dd')}`;
      filename += '.csv';
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Revenue data exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export revenue data');
    } finally {
      setExporting(false);
    }
  };

  const handleStripeSetup = async () => {
    try {
      // First check if account exists, if not create one
      const statusRes = await api.get('/onboarding/stripe/account-status');
      
      if (!statusRes.data?.has_account) {
        await api.post('/onboarding/stripe/create-account');
      }
      
      // Get the onboarding link
      const baseUrl = window.location.origin;
      const response = await api.post('/onboarding/stripe/onboarding-link', null, {
        params: { return_url: `${baseUrl}/partner/revenue` }
      });
      
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Stripe setup error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start Stripe setup. Make sure your partner application is approved.');
    }
  };

  if (!isAuthenticated || !isPartner) return null;

  const summary = revenueData?.summary || {};
  const tickets = revenueData?.tickets || {};
  const experiences = revenueData?.experiences || {};

  return (
    <PartnerLayout 
      title="Revenue Dashboard" 
      subtitle="Track your earnings, commissions, and payouts"
      actions={
        <div className="flex gap-2">
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleExportCSV}
            disabled={exporting}
            data-testid="export-csv-btn"
          >
            {exporting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export CSV
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Payout Status */}
          <PayoutStatusCard payoutInfo={revenueData?.payout_info} onSetupClick={handleStripeSetup} />
          
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <SummaryCard
              title="Total Gross Sales"
              value={`$${summary.total_gross_sales?.toFixed(2) || '0.00'}`}
              subtitle="All revenue generated"
              icon={DollarSign}
              color="indigo"
            />
            <SummaryCard
              title="Platform Fees"
              value={`$${summary.total_platform_fees?.toFixed(2) || '0.00'}`}
              subtitle="Commissions & service fees"
              icon={CreditCard}
              color="orange"
            />
            <SummaryCard
              title="Net Payout"
              value={`$${summary.total_net_payout?.toFixed(2) || '0.00'}`}
              subtitle="Your earnings"
              icon={Wallet}
              color="green"
            />
            <SummaryCard
              title="Pending Payout"
              value={`$${summary.pending_payout?.toFixed(2) || '0.00'}`}
              subtitle="Processing"
              icon={Clock}
              color="purple"
            />
          </div>
          
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full max-w-md grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="experiences">Experiences</TabsTrigger>
              <TabsTrigger value="events">Events</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="space-y-6">
              {/* Revenue Breakdown Row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <RevenueBreakdownChart experiences={experiences} events={tickets} />
                <TopItemsTable items={revenueData?.experience_breakdown || []} type="experience" />
                <TopItemsTable items={revenueData?.event_breakdown || []} type="event" />
              </div>
              
              {/* Recent Transactions */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div>
                    <CardTitle className="text-lg">Recent Transactions</CardTitle>
                    <CardDescription>Your latest sales and payouts</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" className="text-muted-foreground">
                    View All
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent className="p-0">
                  {revenueData?.recent_transactions?.length === 0 ? (
                    <div className="text-center py-12">
                      <DollarSign className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No transactions yet</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Sales will appear here once customers make purchases
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {revenueData?.recent_transactions?.map((tx, i) => (
                        <TransactionRow key={tx.id || i} transaction={tx} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="experiences" className="space-y-6">
              {/* Experience Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <SummaryCard
                  title="Experience Revenue"
                  value={`$${experiences.gross_revenue?.toFixed(2) || '0.00'}`}
                  subtitle={`${experiences.total_sold || 0} sold`}
                  icon={Gift}
                  color="purple"
                />
                <SummaryCard
                  title="Commission Paid"
                  value={`$${experiences.commission_paid?.toFixed(2) || '0.00'}`}
                  subtitle="25% of sales"
                  icon={CreditCard}
                  color="orange"
                />
                <SummaryCard
                  title="Net Earnings"
                  value={`$${experiences.net_payout?.toFixed(2) || '0.00'}`}
                  subtitle="Your payout"
                  icon={Wallet}
                  color="green"
                />
              </div>
              
              {/* Experience Breakdown Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Experience Breakdown</CardTitle>
                  <CardDescription>Revenue by individual experience</CardDescription>
                </CardHeader>
                <CardContent>
                  {revenueData?.experience_breakdown?.length === 0 ? (
                    <div className="text-center py-8">
                      <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No experience sales yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Experience</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Sold</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Gross</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Commission</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Net</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revenueData?.experience_breakdown?.map((exp, i) => (
                            <tr key={exp.id || i} className="border-b last:border-b-0 hover:bg-muted/50">
                              <td className="py-3 px-4">
                                <p className="font-medium">{exp.title}</p>
                                <p className="text-xs text-muted-foreground">{exp.venue_name}</p>
                              </td>
                              <td className="text-right py-3 px-4">{exp.quantity_sold}</td>
                              <td className="text-right py-3 px-4">${exp.gross_revenue?.toFixed(2)}</td>
                              <td className="text-right py-3 px-4 text-orange-600">-${exp.commission?.toFixed(2)}</td>
                              <td className="text-right py-3 px-4 font-bold text-green-600">${exp.net_revenue?.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="events" className="space-y-6">
              {/* Event Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <SummaryCard
                  title="Ticket Revenue"
                  value={`$${tickets.gross_revenue?.toFixed(2) || '0.00'}`}
                  subtitle={`${tickets.total_sold || 0} tickets sold`}
                  icon={Ticket}
                  color="indigo"
                />
                <SummaryCard
                  title="Service Fees"
                  value={`$${tickets.service_fees_collected?.toFixed(2) || '0.00'}`}
                  subtitle="Paid by customers"
                  icon={CreditCard}
                  color="orange"
                />
                <SummaryCard
                  title="Net Earnings"
                  value={`$${tickets.net_payout?.toFixed(2) || '0.00'}`}
                  subtitle="100% of face value"
                  icon={Wallet}
                  color="green"
                />
              </div>
              
              {/* Event Breakdown Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Event Breakdown</CardTitle>
                  <CardDescription>Revenue by individual event</CardDescription>
                </CardHeader>
                <CardContent>
                  {revenueData?.event_breakdown?.length === 0 ? (
                    <div className="text-center py-8">
                      <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No ticket sales yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Event</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Tickets</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revenueData?.event_breakdown?.map((event, i) => (
                            <tr key={event.id || i} className="border-b last:border-b-0 hover:bg-muted/50">
                              <td className="py-3 px-4">
                                <p className="font-medium">{event.title}</p>
                                <p className="text-xs text-muted-foreground">{event.venue_name}</p>
                              </td>
                              <td className="text-right py-3 px-4 text-sm">{event.date}</td>
                              <td className="text-right py-3 px-4">{event.tickets_sold}</td>
                              <td className="text-right py-3 px-4 font-bold text-green-600">${event.net_revenue?.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          
          {/* Fee Explainer */}
          <FeeExplainer />
        </div>
      )}
    </PartnerLayout>
  );
};

export default PartnerRevenue;
